import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy
} from '@angular/core';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { IClaimDetail } from '@module-claims/models/claim.interface';
import { Observable, Subject } from 'rxjs';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngxs/store';
import {
  ActionClaimModalAuditStatusClose,
  ActionClaimModalAuditStatusSave
} from '@module-claims/store/claims-modal.actions';
import { ItemCombobox } from '@models-global/model-combobox-item';
import { Audit340BClaimStatusNum } from '@module-claims/enums/enum-claim-status';
import { IPendingState } from '@module-claims/store/claims.state';
import { ClaimsService } from '@module-claims/services/claims.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-claim-audit-status-modal',
  templateUrl: './claim-audit-status-modal.component.html',
  styleUrls: ['./claim-audit-status-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ClaimAuditStatusModalComponent
  extends DialogContentBase
  implements OnInit, OnDestroy
{
  @Input() claim!: IClaimDetail;

  readonly destroy$ = new Subject();

  auditClaimUpdate$: Observable<IPendingState>;
  form!: UntypedFormGroup;

  statusOptions: ItemCombobox[] = [
    { text: '340B', value: Audit340BClaimStatusNum.IS_340B },
    { text: 'Not 340B', value: Audit340BClaimStatusNum.NOT_340B },
    { text: 'Not Evaluated', value: Audit340BClaimStatusNum.NOT_EVALUATED }
  ];

  constructor(
    private _dialogRef: DialogRef,
    private _fb: UntypedFormBuilder,
    private _store: Store,
    private _claimsService: ClaimsService
  ) {
    super(_dialogRef);
    this.auditClaimUpdate$ = this._claimsService.auditClaimUpdate$;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this._initForm();

    this.auditClaimUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe((updateState) => {
        if (updateState.pending) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      });
  }

  close(): void {
    this._store.dispatch(new ActionClaimModalAuditStatusClose());
  }

  submit(): void {
    if (this.form.pristine || this.form.invalid) {
      for (const key of Object.keys(this.form.controls)) {
        this.form.get(key)?.markAsDirty();
      }
      return;
    }

    const statusVal = this.status.value;
    const reasonTextVal = this.reasonText.value?.trim();

    this._store.dispatch(
      new ActionClaimModalAuditStatusSave({
        intakeId: this.claim.IntakeId!,
        status:
          !!statusVal && this.claim.StatusCategoryId !== statusVal
            ? statusVal
            : undefined,
        reasonText:
          !!reasonTextVal && this.claim.ReasonText?.trim() !== reasonTextVal
            ? reasonTextVal
            : undefined
      })
    );
  }

  private _initForm(): void {
    this.form = this._fb.group({
      status: [this.claim.StatusCategoryId, Validators.required],
      reasonText: [this.claim.ReasonText, Validators.required]
    });
  }

  get status(): UntypedFormControl {
    return this.form.get('status') as UntypedFormControl;
  }
  get reasonText(): UntypedFormControl {
    return this.form.get('reasonText') as UntypedFormControl;
  }
}
